import { configureStore } from '@reduxjs/toolkit';
import { persistStore, persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { combineReducers } from '@reduxjs/toolkit';
import authReducer from './slices/authSlice';
import uiReducer from './slices/uiSlice';
import { validateAuthState } from './slices/authSlice';
import { config } from '@/config/env';
import productsReducer from '@/features/products';
import salesReducer from '@/features/sales';
import collaboratorsReducer from '@/features/collaborators';
import inventoryReducer from '@/features/inventory';
import inputsReducer from '@/features/inputs';
import outputsReducer from '@/features/outputs';
import purchaseOrdersReducer from '@/features/purchaseOrders';
import batchesReducer from '@/features/inventory/batchesSlice';
import variantsReducer from '@/features/products/variantsSlice';
import pricesReducer from '@/features/products/pricesSlice';
import warehousesReducer from '@/features/warehouses';
import grnsReducer from '@/features/grns';
import discountsReducer from '@/features/discounts';
import returnsReducer from '@/features/returns';

// Persist configuration - only persist auth state
const persistConfig = {
  key: 'root',
  storage,
  whitelist: ['auth'], // Only persist auth slice
};

const rootReducer = combineReducers({
  auth: authReducer,
  ui: uiReducer,
  products: productsReducer,
  sales: salesReducer,
  collaborators: collaboratorsReducer,
  inventory: inventoryReducer,
  inputs: inputsReducer,
  outputs: outputsReducer,
  purchaseOrders: purchaseOrdersReducer,
  batches: batchesReducer,
  variants: variantsReducer,
  prices: pricesReducer,
  warehouses: warehousesReducer,
  grns: grnsReducer,
  discounts: discountsReducer,
  returns: returnsReducer,
});

const persistedReducer = persistReducer(persistConfig, rootReducer);

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        // Ignore redux-persist actions
        ignoredActions: ['persist/PERSIST', 'persist/REHYDRATE'],
      },
    }),
  devTools: config.enableDevTools,
});

export const persistor = persistStore(store);

// Validate auth state after rehydration
store.subscribe(() => {
  const state = store.getState();
  // Check if we just rehydrated and state is invalid
  if (state.auth.isAuthenticated && !state.auth.accessToken) {
    store.dispatch(validateAuthState());
  }
});

// Infer types from the store itself
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
